import PropTypes from 'prop-types';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { MoreHorizontal, AlertTriangle, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function StudentsTable({
  students,
  batches,
  selectedStudents,
  onStudentSelection,
  onSelectAllStudents,
  onEditStudent,
  onDeleteStudent
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[50px]">
            <Checkbox 
              checked={
                students.length > 0 && 
                students.every(s => selectedStudents.includes(s.id))
              }
              onCheckedChange={onSelectAllStudents}
              aria-label="Select all students"
            />
          </TableHead>
          <TableHead>Name</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Phone No</TableHead>
          <TableHead>Course Name</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Batch Name</TableHead>
          <TableHead>Course URL</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {students.length > 0 ? (
          students.map((student) => {
            // Find the batch to check if it has a courseId
            const batch = batches.find(b => b.SK === student.batchName);
            const batchHasCourseId = batch && batch.courseId && batch.courseId.trim() !== "";
            
            return (
              <TableRow key={student.id}>
                <TableCell>
                  <Checkbox 
                    checked={selectedStudents.includes(student.id)}
                    onCheckedChange={() => onStudentSelection(student.id)}
                    aria-label={`Select ${student.name}`}
                  />
                </TableCell>
                <TableCell className="font-medium">{student.name}</TableCell>
                <TableCell>{student.email}</TableCell>
                <TableCell>{student.phoneNo}</TableCell>
                <TableCell>{student.courseName}</TableCell>
                <TableCell>
                  {student.isAssigned ? (
                    <Badge variant="outline" className="bg-green-100">Assigned</Badge>
                  ) : (
                    <Badge variant="outline" className="bg-red-100">Unassigned</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={!batchHasCourseId ? "bg-yellow-50" : ""}>
                    {student.batchName}
                    {!batchHasCourseId && (
                      <AlertTriangle className="ml-1 h-3 w-3 text-yellow-500" />
                    )}
                  </Badge>
                </TableCell>
                <TableCell>
                  {batchHasCourseId ? (
                    <a 
                      href={`${batch.courseUrl || "https://classplusapp.com/diy/courses/course-overview?id="}${batch.courseId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      View Course
                    </a>
                  ) : (
                    <span className="text-gray-400">No course ID in batch</span>
                  )}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <span className="sr-only">Open menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onEditStudent(student)}>Edit</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        className="text-red-600"
                        onClick={() => onDeleteStudent(student)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            );
          })
        ) : (
          <TableRow>
            <TableCell colSpan={9} className="text-center py-4">
              No students found for this course
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

StudentsTable.propTypes = {
  students: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    phoneNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    courseName: PropTypes.string.isRequired,
    batchName: PropTypes.string.isRequired,
    isAssigned: PropTypes.bool.isRequired
  })).isRequired,
  batches: PropTypes.arrayOf(PropTypes.shape({
    SK: PropTypes.string.isRequired,
    courseId: PropTypes.string,
    courseUrl: PropTypes.string
  })).isRequired,
  selectedStudents: PropTypes.arrayOf(PropTypes.string).isRequired,
  onStudentSelection: PropTypes.func.isRequired,
  onSelectAllStudents: PropTypes.func.isRequired,
  onEditStudent: PropTypes.func.isRequired,
  onDeleteStudent: PropTypes.func.isRequired
}; 